import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { Button } from '@/ui/rapid-ui'
import { useWhatsAppAccountShowBusinessProfile, useWhatsAppAccountUpdateBusinessProfile } from '@jobilla/nexus-sdk'
import type { WhatsAppAccountUpdateBusinessProfileRequest } from '@jobilla/nexus-sdk'
import {
	ArrowLeftIcon,
	GlobeIcon,
	ImageIcon,
	Loader2Icon,
	MapPinIcon,
	MoreVerticalIcon,
	SaveIcon,
	ShareIcon,
	StoreIcon,
	XIcon,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
	wabaDisplayName: string | null
}

const VERTICAL_VALUES = [
	'PROF_SERVICES',
	'RETAIL',
	'RESTAURANT',
	'HEALTH',
	'BEAUTY',
	'APPAREL',
	'EDU',
	'FINANCE',
	'GROCERY',
	'HOTEL',
	'TRAVEL',
	'EVENT_PLAN',
	'ENTERTAIN',
	'AUTO',
	'GOVT',
	'NONPROFIT',
	'ALCOHOL',
	'ONLINE_GAMBLING',
	'PHYSICAL_GAMBLING',
	'OTC_DRUGS',
	'MATRIMONY_SERVICE',
	'OTHER',
] as const

type VerticalValue = typeof VERTICAL_VALUES[number]

interface BusinessProfileFormState {
	description: string
	address: string
	email: string
	vertical: VerticalValue | ''
	website1: string
	website2: string
	profilePicture: File | null
	profilePicturePreview: string | null
}

const INITIAL_STATE: BusinessProfileFormState = {
	description: '',
	address: '',
	email: '',
	vertical: '',
	website1: '',
	website2: '',
	profilePicture: null,
	profilePicturePreview: null,
}

export function WhatsAppBusinessProfileForm({ wabaDisplayName }: Props) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const [form, setForm] = useState<BusinessProfileFormState>(INITIAL_STATE)
	const [error, setError] = useState('')
	const [success, setSuccess] = useState('')

	const profileQuery = useWhatsAppAccountShowBusinessProfile(organisationId ?? '')

	useEffect(() => {
		const profile = profileQuery.data
		if (!profile) { return }

		setForm((prev) => ({
			...prev,
			description: profile.about ?? '',
			address: profile.address ?? '',
			email: profile.email ?? '',
			vertical: (profile.vertical as VerticalValue | null) ?? '',
			website1: profile.websites?.[0] ?? '',
			website2: profile.websites?.[1] ?? '',
			profilePicturePreview: profile.profilePictureUrl ?? null,
		}))
	}, [profileQuery.data])

	const updateBusinessProfile = useWhatsAppAccountUpdateBusinessProfile({
		onSuccess: () => {
			setSuccess(t('whatsappSettings.businessProfile.updated'))
			setForm((prev) => ({ ...prev, profilePicture: null }))
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
			void profileQuery.refetch()
			logger.track('WhatsApp: Business Profile Updated', {
				category: 'whatsapp',
				data: {
					organisation_id: organisationId,
					fields_updated: [
						form.description && 'description',
						form.address && 'address',
						form.email && 'email',
						form.vertical && 'vertical',
						(form.website1 || form.website2) && 'websites',
						form.profilePicture && 'profile_picture',
					].filter(Boolean),
				},
			})
		},
		onError: (err) => {
			//  The top-level message is generic.
			//  Surface the per-field errors so the user knows which input to fix.
			const data = err.response?.data
			const errors = data && 'errors' in data ? data.errors : undefined
			const firstFieldError = errors ? Object.values(errors).flat()[0] : undefined
			const message = firstFieldError
				?? data?.message
				?? t('whatsappSettings.businessProfile.updateFailed')
			setError(message)
			logger.error(err, {
				category: 'whatsapp',
				data: { action: 'update-business-profile', organisationId, validationErrors: errors },
			})
		},
	})

	const updateField = useCallback(
		<K extends keyof BusinessProfileFormState>(field: K, value: BusinessProfileFormState[K]) => {
			setForm((prev) => ({ ...prev, [field]: value }))
		},
		[],
	)

	const handleProfilePictureSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) { return }

		if (!file.type.startsWith('image/')) {
			setError(t('whatsappSettings.businessProfile.invalidImage'))
			return
		}

		if (file.size > 5 * 1024 * 1024) {
			setError(t('whatsappSettings.businessProfile.imageTooLarge'))
			return
		}

		const previewUrl = URL.createObjectURL(file)
		setForm((prev) => {
			if (prev.profilePicturePreview?.startsWith('blob:')) {
				URL.revokeObjectURL(prev.profilePicturePreview)
			}
			return {
				...prev,
				profilePicture: file,
				profilePicturePreview: previewUrl,
			}
		})
		setError('')
	}, [t])

	const clearProfilePicture = useCallback(() => {
		if (form.profilePicturePreview) {
			URL.revokeObjectURL(form.profilePicturePreview)
		}
		setForm((prev) => ({
			...prev,
			profilePicture: null,
			profilePicturePreview: null,
		}))
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}, [form.profilePicturePreview])

	const handleSubmit = useCallback((event: React.FormEvent) => {
		event.preventDefault()
		if (!organisationId || !form.vertical) { return }

		setError('')
		setSuccess('')

		const websites = [form.website1, form.website2]
			.map((url) => url.trim())
			.filter(Boolean)
			.map((url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`))

		updateBusinessProfile.mutate({
			organisationId,
			data: {
				vertical: form.vertical as WhatsAppAccountUpdateBusinessProfileRequest['vertical'],
				description: form.description || undefined,
				address: form.address || undefined,
				email: form.email || undefined,
				websites: websites.length ? websites : undefined,
				profilePicture: form.profilePicture ?? undefined,
			},
		})
	}, [organisationId, form, updateBusinessProfile])

	const inputClassName =
		'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500'

	const verticalOptions = VERTICAL_VALUES.map((value) => ({
		value,
		label: t(`whatsappSettings.businessProfile.verticals.${value}`),
	}))

	const previewWebsites = [form.website1, form.website2].filter(Boolean)

	return (
		<div className="mt-8 space-y-4">
			<h3 className="text-lg font-medium text-gray-900">
				{t('whatsappSettings.businessProfile.title')}
			</h3>
			<p className="text-sm text-gray-500">
				{t('whatsappSettings.businessProfile.description')}
			</p>

			{error && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
					{error}
				</div>
			)}
			{success && (
				<div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
					{success}
				</div>
			)}

			{profileQuery.isLoading && (
				<div className="flex items-center gap-2 text-sm text-gray-400">
					<Loader2Icon className="size-4 animate-spin" />
					{t('whatsappSettings.businessProfile.loadingProfile')}
				</div>
			)}

			<div className="flex gap-6 items-start">
				<form onSubmit={handleSubmit} className="flex-1 space-y-4 border border-gray-200 rounded-xl p-5">
					<div>
						<label className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700 mb-2">
							{t('whatsappSettings.businessProfile.profilePicture')}
							<span className="text-xs font-normal text-gray-400">
								• {t('whatsappSettings.businessProfile.optional')}
							</span>
						</label>
						<div className="flex items-center gap-4">
							{form.profilePicturePreview
								? (
									<div className="relative">
										<img
											src={form.profilePicturePreview}
											alt="Profile preview"
											className="size-16 rounded-full object-cover border border-gray-200"
										/>
										<button
											type="button"
											onClick={clearProfilePicture}
											className="absolute -top-1 -right-1 size-5 bg-gray-800 text-white rounded-full flex items-center justify-center hover:bg-gray-700"
										>
											<XIcon className="size-3" />
										</button>
									</div>
								)
								: (
									<div className="size-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
										<ImageIcon className="size-6 text-gray-400" />
									</div>
								)}
							<div>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									onChange={handleProfilePictureSelect}
									className="hidden"
								/>
								<Button
									type="subtle"
									size="short"
									onClick={() => fileInputRef.current?.click()}
								>
									{form.profilePicturePreview
										? t('whatsappSettings.businessProfile.changePhoto')
										: t('whatsappSettings.businessProfile.uploadPhoto')}
								</Button>
								<p className="text-xs text-gray-400 mt-1">
									{t('whatsappSettings.businessProfile.photoHint')}
								</p>
							</div>
						</div>
					</div>

					<div>
						<label
							htmlFor="bp-description"
							className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700 mb-1"
						>
							{t('whatsappSettings.businessProfile.descriptionLabel')}
							<span className="text-xs font-normal text-gray-400">
								• {t('whatsappSettings.businessProfile.optional')}
							</span>
						</label>
						<textarea
							id="bp-description"
							value={form.description}
							onChange={(e) => updateField('description', e.target.value)}
							maxLength={512}
							rows={3}
							placeholder={t('whatsappSettings.businessProfile.descriptionPlaceholder')}
							className={`${inputClassName} resize-none`}
						/>
						<p className="mt-1 text-xs text-gray-400 text-right">
							{form.description.length}/512
						</p>
					</div>

					<div>
						<label
							htmlFor="bp-address"
							className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700 mb-1"
						>
							{t('whatsappSettings.businessProfile.addressLabel')}
							<span className="text-xs font-normal text-gray-400">
								• {t('whatsappSettings.businessProfile.optional')}
							</span>
						</label>
						<input
							id="bp-address"
							type="text"
							value={form.address}
							onChange={(e) => updateField('address', e.target.value)}
							maxLength={256}
							placeholder={t('whatsappSettings.businessProfile.addressPlaceholder')}
							className={inputClassName}
						/>
						<p className="mt-1 text-xs text-gray-400 text-right">
							{form.address.length}/256
						</p>
					</div>

					<div>
						<label
							htmlFor="bp-email"
							className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700 mb-1"
						>
							{t('whatsappSettings.businessProfile.emailLabel')}
							<span className="text-xs font-normal text-gray-400">
								• {t('whatsappSettings.businessProfile.optional')}
							</span>
						</label>
						<input
							id="bp-email"
							type="email"
							value={form.email}
							onChange={(e) => updateField('email', e.target.value)}
							maxLength={128}
							placeholder={t('whatsappSettings.businessProfile.emailPlaceholder')}
							className={inputClassName}
						/>
						<p className="mt-1 text-xs text-gray-400 text-right">
							{form.email.length}/128
						</p>
					</div>

					<div>
						<label
							htmlFor="bp-vertical"
							className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700 mb-1"
						>
							{t('whatsappSettings.businessProfile.categoryLabel')}
							<span className="text-xs font-normal text-red-500">*</span>
						</label>
						<select
							id="bp-vertical"
							value={form.vertical}
							onChange={(e) => updateField('vertical', e.target.value as VerticalValue | '')}
							className={`${inputClassName} bg-white`}
						>
							<option value="">{t('whatsappSettings.businessProfile.categoryPlaceholder')}</option>
							{verticalOptions.map((option) => (
								<option key={option.value} value={option.value}>{option.label}</option>
							))}
						</select>
					</div>

					<div className="space-y-2">
						<label className="flex items-baseline gap-1.5 text-sm font-medium text-gray-700">
							{t('whatsappSettings.businessProfile.websitesLabel')}
							<span className="text-xs font-normal text-gray-400">
								• {t('whatsappSettings.businessProfile.optional')}
							</span>
						</label>
						<div>
							<input
								type="url"
								value={form.website1}
								onChange={(e) => updateField('website1', e.target.value)}
								maxLength={256}
								placeholder={t('whatsappSettings.businessProfile.website1Placeholder')}
								className={inputClassName}
							/>
							<p className="mt-1 text-xs text-gray-400 text-right">
								{form.website1.length}/256
							</p>
						</div>
						<div>
							<input
								type="url"
								value={form.website2}
								onChange={(e) => updateField('website2', e.target.value)}
								maxLength={256}
								placeholder={t('whatsappSettings.businessProfile.website2Placeholder')}
								className={inputClassName}
							/>
							<p className="mt-1 text-xs text-gray-400 text-right">
								{form.website2.length}/256
							</p>
						</div>
					</div>

					<div className="flex justify-end pt-2">
						<Button
							type="neutral"
							size="short"
							submit
							disabled={updateBusinessProfile.isPending || !form.vertical}
						>
							{updateBusinessProfile.isPending
								? (
									<span className="flex items-center gap-2">
										<Loader2Icon className="size-4 animate-spin" />
										{t('whatsappSettings.businessProfile.saving')}
									</span>
								)
								: (
									<span className="flex items-center gap-1.5">
										<SaveIcon className="size-4" />
										{t('whatsappSettings.businessProfile.save')}
									</span>
								)}
						</Button>
					</div>
				</form>

				{/* Preview card */}
				<div className="w-[300px] shrink-0 sticky top-6">
					<p className="text-xs text-gray-400 text-center mb-2">
						{t('whatsappSettings.businessProfile.preview')}
					</p>
					<div className="rounded-2xl overflow-hidden shadow-md border border-gray-200 bg-[#f0f2f5]">
						{/* Top bar */}
						<div className="bg-white flex items-center justify-between px-4 py-3">
							<ArrowLeftIcon className="size-4 text-gray-400" />
							<MoreVerticalIcon className="size-4 text-gray-400" />
						</div>

						{/* Hero */}
						<div className="bg-white flex flex-col items-center px-6 pt-4 pb-5 border-t border-gray-100">
							{form.profilePicturePreview
								? (
									<img
										src={form.profilePicturePreview}
										alt=""
										className="size-20 rounded-full object-cover shadow"
									/>
								)
								: (
									<div className="size-20 rounded-full bg-gray-200 flex items-center justify-center shadow">
										<ImageIcon className="size-9 text-gray-400" />
									</div>
								)}
							<p className="mt-3 text-base font-bold text-gray-900 text-center">
								{wabaDisplayName || t('whatsappSettings.businessProfile.previewBusinessName')}
							</p>
							{form.description && (
								<p className="mt-1 text-xs text-gray-500 text-center line-clamp-3">
									{form.description}
								</p>
							)}
							<button
								type="button"
								tabIndex={-1}
								className="mt-4 flex flex-col items-center gap-1 px-8 py-2 rounded-xl border border-gray-200 text-gray-600 pointer-events-none"
							>
								<ShareIcon className="size-4" />
								<span className="text-xs">{t('whatsappSettings.businessProfile.previewShare')}</span>
							</button>
						</div>

						{/* Info rows */}
						{(form.vertical || form.address || previewWebsites.length > 0) && (
							<div className="mt-2 bg-white divide-y divide-gray-100">
								{form.vertical && (
									<div className="flex items-center gap-3 px-4 py-3">
										<StoreIcon className="size-4 text-gray-500 shrink-0" />
										<p className="text-xs text-gray-700">
											{verticalOptions.find((option) => option.value === form.vertical)?.label}
										</p>
									</div>
								)}
								{form.address && (
									<div className="flex items-start gap-3 px-4 py-3">
										<MapPinIcon className="size-4 text-[#008069] mt-0.5 shrink-0" />
										<p className="text-xs text-[#008069] leading-snug">{form.address}</p>
									</div>
								)}
								{previewWebsites.map((website) => (
									<div key={website} className="flex items-start gap-3 px-4 py-3">
										<GlobeIcon className="size-4 text-[#008069] mt-0.5 shrink-0" />
										<p className="text-xs text-[#008069] break-all">{website}</p>
									</div>
								))}
							</div>
						)}

						{/* Footer */}
						<p className="px-4 py-3 text-[10px] text-gray-400 leading-snug">
							{t('whatsappSettings.businessProfile.previewDisclaimer')}
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}
